import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { X, Eye, EyeOff, Printer, CheckCircle, Circle, Trash2, Plus, ChevronDown, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import QuizPrintView from './QuizPrintView';
import './QuizPrintView.css';

/* ── Question type metadata ── */
const QUESTION_TYPES = [
    { value: 'mcq', label: 'Multiple Choice', badge: 'bg-blue-100 text-blue-700' },
    { value: 'true_false', label: 'True / False', badge: 'bg-purple-100 text-purple-700' },
    { value: 'short_answer', label: 'Short Answer', badge: 'bg-amber-100 text-amber-700' },
];

const typeLabel = (type) => QUESTION_TYPES.find(t => t.value === type)?.label || type;
const typeBadge = (type) => QUESTION_TYPES.find(t => t.value === type)?.badge || 'bg-slate-100 text-slate-700';

/**
 * Normalize question data when type changes.
 * Prevents stale choices/answer shapes from leaking between types.
 */
const normalizeQuestionForType = (q, newType) => {
    const base = { ...q, type: newType };
    switch (newType) {
        case 'mcq':
            return {
                ...base,
                choices: base.choices?.length ? base.choices : ['A) ', 'B) ', 'C) ', 'D) '],
                answer: base.answer || 'A',
            };
        case 'true_false':
            return {
                ...base,
                choices: undefined,
                answer: ['true', 'false'].includes(base.answer?.toLowerCase?.())
                    ? base.answer.toLowerCase()
                    : 'true',
            };
        case 'short_answer':
            return {
                ...base,
                choices: undefined,
                answer: base.answer?.length === 1 ? '' : (base.answer || ''),
            };
        default:
            return base;
    }
};

/** Tag each question with a stable key for React reconciliation */
const tagQuestions = (questions) =>
    (questions || []).map((q, i) => ({ ...q, _key: q._key || q.id || `q-${i}-${Date.now()}` }));

/* ── Editable Question Card ── */
const EditableQuestion = ({ q, index, onChange, onDelete, showAnswers, isEditing }) => {
    const handleTypeChange = (newType) => {
        if (newType === q.type) return;
        onChange(normalizeQuestionForType(q, newType));
    };

    const updateField = (field, value) => {
        onChange({ ...q, [field]: value });
    };

    const updateChoice = (ci, value) => {
        const newChoices = [...(q.choices || [])];
        newChoices[ci] = value;
        onChange({ ...q, choices: newChoices });
    };

    const deleteChoice = (ci) => {
        const newChoices = (q.choices || []).filter((_, i) => i !== ci);
        onChange({ ...q, choices: newChoices });
    };

    const addChoice = () => {
        const letter = String.fromCharCode(65 + (q.choices?.length || 0));
        onChange({ ...q, choices: [...(q.choices || []), `${letter}) `] });
    };

    return (
        <div className="quiz-question mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200 group">
            <div className="flex items-start gap-3 mb-3">
                <span className="flex-shrink-0 w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center text-sm font-bold mt-1">
                    {index + 1}
                </span>
                <div className="flex-1 min-w-0">
                    {/* Type + Points row */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {isEditing ? (
                            <>
                                <div className="relative">
                                    <select
                                        value={q.type}
                                        onChange={e => handleTypeChange(e.target.value)}
                                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 appearance-none cursor-pointer pr-6 ${typeBadge(q.type)}`}
                                    >
                                        {QUESTION_TYPES.map(t => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                    <ChevronDown size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500" />
                                </div>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min={1}
                                        max={100}
                                        value={q.points || 10}
                                        onChange={e => updateField('points', Math.max(1, parseInt(e.target.value) || 1))}
                                        className="w-12 text-xs font-medium px-1.5 py-0.5 rounded-md border border-slate-200 text-center focus:outline-none focus:border-blue-400"
                                    />
                                    <span className="text-xs text-slate-400">pts</span>
                                </div>
                            </>
                        ) : (
                            <>
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadge(q.type)}`}>
                                    {typeLabel(q.type)}
                                </span>
                                <span className="text-xs text-slate-400">{q.points || 10} pts</span>
                            </>
                        )}
                    </div>

                    {/* Prompt */}
                    {isEditing ? (
                        <textarea
                            value={q.prompt || ''}
                            onChange={e => updateField('prompt', e.target.value)}
                            rows={2}
                            className="w-full text-slate-800 font-medium text-sm p-2 rounded-lg border border-slate-200 resize-vertical focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            placeholder="Enter question text..."
                        />
                    ) : (
                        <p className="text-slate-800 font-medium">{q.prompt}</p>
                    )}
                </div>

                {isEditing && (
                    <button
                        onClick={onDelete}
                        className="flex-shrink-0 p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete question"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
            </div>

            {/* MCQ Choices */}
            {q.type === 'mcq' && (
                <div className="ml-11 space-y-2 mt-2">
                    {(q.choices || []).map((choice, ci) => {
                        const letter = choice.match(/^([A-D])\)/)?.[1] || String.fromCharCode(65 + ci);
                        const isCorrect = showAnswers && q.answer?.toUpperCase() === letter.toUpperCase();
                        return (
                            <div key={ci} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-300 text-emerald-800 font-medium' : 'text-slate-600'}`}>
                                {isCorrect ? (
                                    <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                                ) : (
                                    <Circle size={16} className="text-slate-300 flex-shrink-0" />
                                )}
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={choice}
                                        onChange={e => updateChoice(ci, e.target.value)}
                                        className="flex-1 bg-transparent border-b border-dashed border-slate-200 focus:border-blue-400 outline-none py-0.5 text-sm"
                                    />
                                ) : (
                                    <span>{choice}</span>
                                )}
                                {isEditing && (
                                    <button
                                        onClick={() => deleteChoice(ci)}
                                        className="text-slate-300 hover:text-red-400 transition-colors"
                                        title="Remove choice"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {isEditing && (q.choices?.length || 0) < 6 && (
                        <button
                            onClick={addChoice}
                            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors"
                        >
                            <Plus size={12} /> Add choice
                        </button>
                    )}
                </div>
            )}

            {/* True/False choices */}
            {q.type === 'true_false' && (
                <div className="ml-11 space-y-2 mt-2">
                    {['True', 'False'].map(opt => {
                        const isCorrect = showAnswers && q.answer?.toLowerCase() === opt.toLowerCase();
                        return (
                            <div key={opt} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-300 text-emerald-800 font-medium' : 'text-slate-600'}`}>
                                {isEditing ? (
                                    <button
                                        onClick={() => updateField('answer', opt.toLowerCase())}
                                        className="flex-shrink-0"
                                        title={`Set ${opt} as correct answer`}
                                    >
                                        {q.answer?.toLowerCase() === opt.toLowerCase() ? (
                                            <CheckCircle size={16} className="text-emerald-500" />
                                        ) : (
                                            <Circle size={16} className="text-slate-300 hover:text-emerald-400" />
                                        )}
                                    </button>
                                ) : (
                                    isCorrect ? (
                                        <CheckCircle size={16} className="text-emerald-500 flex-shrink-0" />
                                    ) : (
                                        <Circle size={16} className="text-slate-300 flex-shrink-0" />
                                    )
                                )}
                                {opt}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Short Answer */}
            {q.type === 'short_answer' && (
                <div className="ml-11 mt-2">
                    {isEditing ? (
                        <div className="space-y-2">
                            <label className="text-xs text-slate-500">Answer key</label>
                            <input
                                type="text"
                                value={q.answer || ''}
                                onChange={e => updateField('answer', e.target.value)}
                                className="w-full p-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                                placeholder="Expected answer..."
                            />
                        </div>
                    ) : (
                        <>
                            <div className="border-b-2 border-dashed border-slate-300 h-16 rounded" />
                            {showAnswers && (
                                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                                    <span className="font-semibold">Answer Key: </span>{q.answer}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

/* ── Main QuizModal ── */
const QuizModal = ({ quiz: initialQuiz, isOpen, onClose, onSave }) => {
    const [showAnswers, setShowAnswers] = useState(false);
    const [isPrintMode, setIsPrintMode] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [questions, setQuestions] = useState([]);
    const [quizTitle, setQuizTitle] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState(null); // null | 'saved' | 'error'

    // Hydrate editable state when modal opens or quiz prop changes.
    useEffect(() => {
        if (isOpen && initialQuiz) {
            setQuestions(tagQuestions(initialQuiz.questions));
            setQuizTitle(initialQuiz.title || 'Quiz');
            setIsEditing(false);
            setShowAnswers(false);
            setIsDirty(false);
            setSaveStatus(null);
        }
        if (!isOpen) {
            setQuestions([]);
            setQuizTitle('');
            setIsEditing(false);
            setShowAnswers(false);
            setIsDirty(false);
            setSaveStatus(null);
        }
    }, [isOpen, initialQuiz]);

    // Compute derived quiz object for print / display
    const editedQuiz = useMemo(() => ({
        ...(initialQuiz || {}),
        title: quizTitle,
        questions,
        total_points: questions.reduce((sum, q) => sum + (q.points || 10), 0),
    }), [initialQuiz, quizTitle, questions]);

    const handlePrint = useCallback(() => {
        setIsPrintMode(true);
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.print();
                setIsPrintMode(false);
            });
        });
    }, []);

    const updateQuestion = useCallback((index, updated) => {
        setQuestions(prev => prev.map((q, i) =>
            i === index ? { ...updated, _key: q._key } : q
        ));
        setIsDirty(true);
        setSaveStatus(null);
    }, []);

    const deleteQuestion = useCallback((index) => {
        setQuestions(prev => {
            if (prev.length <= 1) return prev;
            return prev.filter((_, i) => i !== index);
        });
        setIsDirty(true);
        setSaveStatus(null);
    }, []);

    // Strip internal keys before saving
    const stripKeys = (qs) => qs.map(q => { const c = { ...q }; delete c._key; return c; });

    const handleSave = useCallback(async () => {
        if (!onSave || isSaving) return;
        setIsSaving(true);
        setSaveStatus(null);
        try {
            const quizToSave = {
                ...(initialQuiz || {}),
                title: quizTitle,
                questions: stripKeys(questions),
                total_points: questions.reduce((sum, q) => sum + (q.points || 10), 0),
            };
            await onSave(quizToSave);
            setIsDirty(false);
            setSaveStatus('saved');
        } catch {
            setSaveStatus('error');
        } finally {
            setIsSaving(false);
        }
    }, [onSave, isSaving, initialQuiz, quizTitle, questions]);

    // Also mark dirty when title changes
    const handleTitleChange = useCallback((e) => {
        setQuizTitle(e.target.value);
        setIsDirty(true);
        setSaveStatus(null);
    }, []);

    // Early return AFTER all hooks
    if (!isOpen || !initialQuiz) return null;

    return (
        <>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-slate-200 flex items-center justify-between shrink-0">
                            <div className="min-w-0">
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={quizTitle}
                                        onChange={handleTitleChange}
                                        className="text-xl font-bold text-slate-900 border-b-2 border-blue-400 outline-none bg-transparent w-full"
                                    />
                                ) : (
                                    <h2 className="text-xl font-bold text-slate-900">{editedQuiz.title}</h2>
                                )}
                                <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                                    {editedQuiz.grade_level && <span>Grade {editedQuiz.grade_level}</span>}
                                    <span>•</span>
                                    <span>{questions.length} Questions</span>
                                    <span>•</span>
                                    <span>{editedQuiz.total_points} Points</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={() => setIsEditing(!isEditing)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${isEditing
                                        ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {isEditing ? '✓ Done Editing' : '✏️ Edit'}
                                </button>
                                <button
                                    onClick={() => setShowAnswers(!showAnswers)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${showAnswers
                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {showAnswers ? <EyeOff size={16} /> : <Eye size={16} />}
                                    {showAnswers ? 'Hide Key' : 'Answer Key'}
                                </button>
                                <button
                                    onClick={handlePrint}
                                    className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors"
                                >
                                    <Printer size={16} />
                                    Print
                                </button>
                                {isDirty && onSave && (
                                    <button
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
                                    >
                                        <Save size={16} />
                                        {isSaving ? 'Saving...' : 'Save'}
                                    </button>
                                )}
                                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 ml-1">
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Questions */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-2" style={{ minHeight: 0 }}>
                            {questions.map((q, i) => (
                                <EditableQuestion
                                    key={q._key}
                                    q={q}
                                    index={i}
                                    onChange={(updated) => updateQuestion(i, updated)}
                                    onDelete={() => deleteQuestion(i)}
                                    showAnswers={showAnswers}
                                    isEditing={isEditing}
                                />
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-100 text-center text-xs shrink-0">
                            {saveStatus === 'saved' ? (
                                <span className="text-emerald-600 font-medium">✓ Edits saved successfully</span>
                            ) : saveStatus === 'error' ? (
                                <span className="text-red-500 font-medium">Failed to save — try again</span>
                            ) : isDirty ? (
                                <span className="text-amber-600 font-medium">Unsaved changes</span>
                            ) : isEditing ? (
                                <span className="text-slate-400">{`Editing ${questions.length} questions • ${editedQuiz.total_points} total points`}</span>
                            ) : (
                                <span className="text-slate-400">{editedQuiz.saved ? '✓ Saved to My Documents' : 'Quiz auto-saved to your library'}</span>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            </AnimatePresence>

            {/* Print-only static view — always uses editedQuiz */}
            {isPrintMode && (
                <QuizPrintView quiz={editedQuiz} showAnswers={showAnswers} />
            )}
        </>
    );
};

export default QuizModal;
